import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../utility/useSession";
import {
    api,
    type PickupLocationResponse,
    type CreatePickupLocation,
    type UpdatePickupLocation,
    PickupLocationEntityType
} from "@/logic/api";
import { AddEditPickupLocationModal } from "./AddEditPickupLocationModal";
import { PickupLocationListItem } from "./PickupLocationListItem";

export const PickupLocationManagementCard: React.FC = () => {
    const { t } = useTranslation();
    const session = useContext(SessionContext);
    const [locations, setLocations] = useState<PickupLocationResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string>("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState<PickupLocationResponse | undefined>();
    const [deletingLocation, setDeletingLocation] = useState<PickupLocationResponse | undefined>();

    const listPickupLocationsHook = api.hooks.pickupLocation.listPickupLocations();
    const createPickupLocationHook = api.hooks.pickupLocation.createPickupLocation();
    const updatePickupLocationHook = api.hooks.pickupLocation.updatePickupLocation();
  const deletePickupLocationHook = api.hooks.pickupLocation.deletePickupLocation();

    const loadLocations = async () => {
        setLoading(true);
        setError("");

        return new Promise<void>((resolve) => {
            listPickupLocationsHook.send(
                session.current.context,
                { includeInactive: true },
                (result) => {
                    if (result) {
                        setLocations(result);
                    } else if (listPickupLocationsHook.err) {
                        console.error("Error loading pickup locations:", listPickupLocationsHook.err);
                        setError(t("settings.shuttlePickup.pickupLocations.errors.loadFailed"));
                    }
                    setLoading(false);
                    resolve();
                }
            );
        });
    };

    useEffect(() => {
        loadLocations();
    }, []);

    const handleAdd = async (data: {
        name: string;
        latitude: number | null;
        longitude: number | null;
        isActive: boolean;
        matchingEntityType: PickupLocationEntityType | null;
        matchingEntityReference: string | null;
    }) => {
        setIsSaving(true);
        try {
            const params: CreatePickupLocation = {
                name: data.name,
              coordinates: data.latitude != null && data.longitude != null
                ? { latitude: data.latitude, longitude: data.longitude }
                : undefined,
                isActive: data.isActive,
              matchingEntity: data.matchingEntityType != null && data.matchingEntityReference != null
                ? { matchingEntityType: data.matchingEntityType, matchingEntityReference: data.matchingEntityReference }
                : undefined
            };

            await new Promise<PickupLocationResponse>((resolve, reject) => {
                createPickupLocationHook.send(
                    session.current.context,
                    params,
                    (result) => {
                        if (result) {
                            setLocations(prev => [...prev, result]);
                            resolve(result);
                        } else {
                            reject(createPickupLocationHook.err);
                        }
                    }
                );
            });
        } catch (err) {
            console.error("Error creating pickup location:", err);
            throw new Error(t("settings.shuttlePickup.pickupLocations.errors.saveFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = async (data: {
        name: string;
        latitude: number | null;
        longitude: number | null;
        isActive: boolean;
        matchingEntityType: PickupLocationEntityType | null;
        matchingEntityReference: string | null;
    }) => {
        if (!editingLocation?.uuid) return;

        setIsSaving(true);
        try {
            const params: UpdatePickupLocation = {
                uuid: editingLocation.uuid,
                name: data.name,
              coordinates: data.latitude != null && data.longitude != null
                ? { latitude: data.latitude, longitude: data.longitude }
                : undefined,
                isActive: data.isActive,
              matchingEntity: data.matchingEntityType != null && data.matchingEntityReference != null
                ? { matchingEntityType: data.matchingEntityType, matchingEntityReference: data.matchingEntityReference }
                : undefined
            };

            await new Promise<PickupLocationResponse>((resolve, reject) => {
                updatePickupLocationHook.send(
                    session.current.context,
                    params,
                    (result) => {
                        if (result) {
                            setLocations(prev =>
                                prev.map(l => l.uuid === editingLocation.uuid ? result : l)
                            );
                            resolve(result);
                        } else {
                            reject(updatePickupLocationHook.err);
                        }
                    }
                );
            });

            setEditingLocation(undefined);
        } catch (err) {
            console.error("Error updating pickup location:", err);
            throw new Error(t("settings.shuttlePickup.pickupLocations.errors.saveFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingLocation?.uuid) return;

        setIsSaving(true);
        try {
          await new Promise<boolean>((resolve, reject) => {
            deletePickupLocationHook.send(
                    session.current.context,
              { uuid: deletingLocation.uuid },
                    (result) => {
                        if (result) {
                            resolve(result);
                        } else {
                          reject(deletePickupLocationHook.err);
                        }
                    }
                );
            });

            setLocations(prev => prev.filter(l => l.uuid !== deletingLocation.uuid));
            setDeletingLocation(undefined);
        } catch (err) {
            console.error("Error deleting pickup location:", err);
            setError(t("settings.shuttlePickup.pickupLocations.errors.deleteFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="pickup-location-management-card">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
                        {t("settings.shuttlePickup.pickupLocations.heading")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("settings.shuttlePickup.pickupLocations.description")}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddModal(true)}
                    disabled={isSaving}
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: (theme) => theme.palette.brand.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.brand.dark,
                            color: (theme) => theme.palette.primary.contrastText,
                        },
                        "&:active": { transform: "scale(0.97)" },
                        "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
                    }}
                    disableRipple
                    disableElevation
                    data-testid="add-pickup-location-button"
                >
                    {t("settings.shuttlePickup.pickupLocations.addLocation")}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {locations.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        py: 6,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        bgcolor: "#f9f9f9"
                    }}
                    data-testid="pickup-location-empty-state"
                >
                    <Typography variant="body1" color="text.secondary">
                        {t("settings.shuttlePickup.pickupLocations.noLocations")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {locations.map((location) => (
                        <PickupLocationListItem
                            key={location.uuid}
                            location={location}
                            onEdit={setEditingLocation}
                            onDelete={setDeletingLocation}
                            disabled={isSaving}
                        />
                    ))}
                </Box>
            )}

            <AddEditPickupLocationModal
                key={editingLocation?.uuid ?? (showAddModal ? "new" : "closed")}
                open={showAddModal || Boolean(editingLocation)}
                onClose={() => {
                    setShowAddModal(false);
                    setEditingLocation(undefined);
                }}
                onSave={editingLocation ? handleEdit : handleAdd}
                location={editingLocation}
                isLoading={isSaving}
            />

            <Dialog
                open={Boolean(deletingLocation)}
                onClose={() => setDeletingLocation(undefined)}
                maxWidth="sm"
                fullWidth
                data-testid="delete-pickup-location-confirmation"
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("settings.shuttlePickup.pickupLocations.deleteDialog.title")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("settings.shuttlePickup.pickupLocations.deleteDialog.message", {
                            name: deletingLocation?.name
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setDeletingLocation(undefined)}
                        disabled={isSaving}
                        variant="text"
                        sx={{
                            transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                            "&:active": { transform: "scale(0.97)" },
                            "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
                            "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
                        }}
                        disableRipple
                        disableElevation
                        data-testid="cancel-delete-pickup-location-button"
                    >
                        {t("brand.actions.cancel")}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        disabled={isSaving}
                        sx={{
                            fontWeight: "bold",
                            transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                            "&:active": { transform: "scale(0.97)" },
                            "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
                        }}
                        disableRipple
                        disableElevation
                        data-testid="confirm-delete-pickup-location-button"
                    >
                        {isSaving ? t("brand.actions.deleting") : t("settings.shuttlePickup.pickupLocations.deleteDialog.title")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
